"use client"

import * as React from "react"
import { useState, forwardRef, InputHTMLAttributes, useEffect, useRef, memo } from "react"
import { cn } from "@/lib/utils"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"

// Memoized icons - prevents recreation on every render
const InfoCircleIcon = memo(function InfoCircleIcon() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 20 20" fill="none" className="size-4 cursor-help text-gray-400 transition-colors hover:text-gray-600">
            <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="2" strokeLinecap="square" />
            <path d="M9 10H10V14H11" stroke="currentColor" strokeWidth="2" strokeLinecap="square" />
            <rect x="9" y="5" width="2" height="2" fill="currentColor" />
        </svg>
    )
})

const EyeIcon = memo(function EyeIcon() {
    return (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
    )
})

const EyeOffIcon = memo(function EyeOffIcon() {
    return (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
            />
        </svg>
    )
})

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "className"> {
    /**
     * Input variant - choose based on background context:
     *
     * - 'standard': Gray background (#f5f5f5), no border
     *   USE ON: White background cards, modals, settings panels
     *   Example: Settings pages, profile forms, dashboard cards
     *
     * - 'floating': Gray background with animated floating label
     *   USE ON: Auth forms (login, signup, forgot password)
     *   Example: Login page, signup page
     *
     * - 'outlined': White background with gray border
     *   USE ON: Gray/colored backgrounds, inline forms, search bars
     *   Example: Filters on gray backgrounds, embedded forms
     *
     * PATTERN: Match input background to create contrast with container.
     * White container → gray input (standard/floating)
     * Gray container → white input (outlined)
     */
    variant?: "floating" | "standard" | "outlined"
    /** Label text displayed above or floating within the input */
    label?: string
    /** Error message to display below the input */
    error?: string
    /** Helper text displayed below the input */
    helperText?: string
    /** Tooltip text shown when hovering the info icon */
    infoTooltip?: string
    /** Icon displayed on the left side of the input */
    icon?: React.ReactNode
    /** Show password visibility toggle button */
    showPasswordToggle?: boolean
}

// Style variants for the input element
const inputVariantStyles = {
    // Standard variant: gray background, no border (landing/auth pages)
    standard: (error?: string) =>
        cn(
            "h-12 w-full rounded-[16px]",
            "bg-[#f5f5f5] text-base font-semibold tracking-[0.009em] text-gray-900",
            "placeholder:font-normal placeholder:text-gray-400",
            "border-0 outline-none",
            "transition-all duration-200 ease-out",
            "focus:bg-white focus:ring-2 focus:ring-inset focus:ring-gray-900",
            "disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400",
            error && "ring-2 ring-inset ring-red-500 focus:ring-red-500",
            "[&:-webkit-autofill]:animate-[onAutoFillStart] [&:not(:-webkit-autofill)]:animate-[onAutoFillCancel]"
        ),
    // Floating variant: same as standard but taller for label space
    floating: (error?: string) =>
        cn(
            "h-14 w-full rounded-[16px]",
            "bg-[#f5f5f5] text-base font-semibold tracking-[0.009em] text-gray-900",
            "placeholder:font-normal placeholder:text-gray-400",
            "border-0 outline-none",
            "transition-all duration-200 ease-out",
            "focus:bg-white focus:ring-2 focus:ring-inset focus:ring-gray-900",
            "disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400",
            error && "ring-2 ring-inset ring-red-500 focus:ring-red-500",
            "[&:-webkit-autofill]:animate-[onAutoFillStart] [&:not(:-webkit-autofill)]:animate-[onAutoFillCancel]"
        ),
    // Outlined variant: white background with border (dashboard/app pages)
    outlined: (error?: string) =>
        cn(
            "h-11 w-full rounded-xl",
            "bg-white text-base leading-normal text-gray-900",
            "placeholder:text-gray-400",
            "border border-gray-200 outline-none",
            "transition-all duration-200 ease-out",
            "focus:border-gray-400 focus:ring-2 focus:ring-gray-400/20",
            "disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500",
            error && "border-red-500 focus:border-red-500 focus:ring-red-500/20"
        )
}

/**
 * Unified Input component with three variants:
 * - `standard`: Label above, gray background - for landing/auth pages
 * - `floating`: Animated floating label - for auth forms
 * - `outlined`: White background with border - for dashboard/app pages
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(({ variant = "standard", label, error, helperText, infoTooltip, icon, showPasswordToggle, type = "text", value, onChange, required, disabled, defaultValue, ...props }, ref) => {
    const [isFocused, setIsFocused] = useState(false)
    const [showPassword, setShowPassword] = useState(false)
    const [isAutofilled, setIsAutofilled] = useState(false)
    const internalRef = useRef<HTMLInputElement>(null)
    const inputRef = (ref as React.RefObject<HTMLInputElement>) || internalRef

    // Only run autofill detection for floating variant
    useEffect(() => {
        if (variant !== "floating") return

        const input = inputRef.current
        if (!input) return

        // Check for existing value on mount (handles browser restore, defaultValue)
        if (input.value) {
            setIsAutofilled(true)
        }

        // Listen for autofill animation (browsers trigger animation on autofill)
        const handleAnimationStart = (e: AnimationEvent) => {
            if (e.animationName === "onAutoFillStart") {
                setIsAutofilled(true)
            } else if (e.animationName === "onAutoFillCancel") {
                setIsAutofilled(false)
            }
        }

        input.addEventListener("animationstart", handleAnimationStart)
        return () => input.removeEventListener("animationstart", handleAnimationStart)
    }, [variant, inputRef])

    // Determine if label should float (only computed for floating variant)
    const hasValue = value !== undefined && value !== ""
    const hasDefaultValue = defaultValue !== undefined && defaultValue !== ""
    const isFloating = variant === "floating" && (isFocused || hasValue || hasDefaultValue || isAutofilled)
    const inputType = showPasswordToggle && showPassword ? "text" : type

    // Floating variant - animated floating label
    if (variant === "floating") {
        return (
            <div className="relative">
                <div className="relative">
                    {/* Left icon */}
                    {icon && <div className="pointer-events-none absolute inset-y-0 left-0 z-10 flex items-center pl-4">{icon}</div>}

                    {/* Input */}
                    <input
                        ref={inputRef}
                        type={inputType}
                        value={value}
                        defaultValue={defaultValue}
                        onChange={(e) => {
                            setIsAutofilled(!!e.target.value)
                            onChange?.(e)
                        }}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                        disabled={disabled}
                        required={required}
                        className={cn(inputVariantStyles.floating(error), "pb-3 pt-6", icon ? "pl-12 pr-4" : "px-4", showPasswordToggle && "pr-12")}
                        placeholder=""
                        {...props}
                    />

                    {/* Floating Label */}
                    {label && (
                        <label htmlFor={props.id} className={cn("pointer-events-none absolute origin-top-left font-medium", "transition-all duration-200 ease-out", icon ? "left-12" : "left-4", isFloating ? "top-[10px] text-xs text-gray-500" : "top-1/2 -translate-y-1/2 text-base text-gray-400")}>
                            {label}
                            {required && "*"}
                        </label>
                    )}

                    {/* Password Toggle Button */}
                    {showPasswordToggle && (
                        <button type="button" onClick={() => setShowPassword((prev) => !prev)} className="absolute inset-y-0 right-0 flex items-center pr-4 text-gray-400 transition-colors hover:text-gray-600" tabIndex={-1} aria-label={showPassword ? "Hide password" : "Show password"}>
                            {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                        </button>
                    )}
                </div>

                {/* Error Message */}
                {error && (
                    <p className="mt-1.5 text-sm text-red-500" role="alert">
                        {error}
                    </p>
                )}
            </div>
        )
    }

    // Outlined variant - white background with border (dashboard/app pages)
    if (variant === "outlined") {
        return (
            <div className="flex flex-col gap-1.5">
                {/* Label row */}
                {label && (
                    <div className="flex items-center gap-1">
                        <label htmlFor={props.id} className="text-sm font-medium text-gray-700">
                            {label}
                            {required && <span className="text-red-500">*</span>}
                        </label>
                        {infoTooltip && (
                            <TooltipProvider delayDuration={200}>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <span className="inline-flex">
                                            <InfoCircleIcon />
                                        </span>
                                    </TooltipTrigger>
                                    <TooltipContent side="top" className="max-w-[280px] text-sm">
                                        {infoTooltip}
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        )}
                    </div>
                )}

                {/* Input wrapper */}
                <div className="relative">
                    {/* Left icon */}
                    {icon && <div className="pointer-events-none absolute inset-y-0 left-0 z-10 flex items-center pl-3">{icon}</div>}

                    {/* Input */}
                    <input
                        ref={inputRef}
                        type={inputType}
                        value={value}
                        defaultValue={defaultValue}
                        onChange={onChange}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                        disabled={disabled}
                        required={required}
                        className={cn(inputVariantStyles.outlined(error), icon ? "pl-10 pr-4" : "px-4", showPasswordToggle && "pr-12")}
                        {...props}
                    />

                    {/* Password Toggle Button */}
                    {showPasswordToggle && (
                        <button type="button" onClick={() => setShowPassword((prev) => !prev)} className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 transition-colors hover:text-gray-600" tabIndex={-1} aria-label={showPassword ? "Hide password" : "Show password"}>
                            {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                        </button>
                    )}
                </div>

                {/* Helper text or Error message */}
                {(helperText || error) && (
                    <p className={cn("text-[13px]", error ? "text-red-500" : "text-gray-500")} role={error ? "alert" : undefined}>
                        {error || helperText}
                    </p>
                )}
            </div>
        )
    }

    // Standard variant (label above, gray background)
    return (
        <div className="flex flex-col gap-2">
            {/* Label row */}
            {label && (
                <div className="flex items-center gap-1">
                    <label htmlFor={props.id} className="text-sm font-semibold text-gray-900">
                        {label}
                        {required && "*"}
                    </label>
                    {infoTooltip && (
                        <TooltipProvider delayDuration={200}>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <span className="inline-flex">
                                        <InfoCircleIcon />
                                    </span>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-[280px] text-sm">
                                    {infoTooltip}
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    )}
                </div>
            )}

            {/* Input wrapper */}
            <div className="relative">
                {/* Left icon */}
                {icon && <div className="pointer-events-none absolute inset-y-0 left-0 z-10 flex items-center pl-4">{icon}</div>}

                {/* Input */}
                <input
                    ref={inputRef}
                    type={inputType}
                    value={value}
                    defaultValue={defaultValue}
                    onChange={onChange}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    disabled={disabled}
                    required={required}
                    className={cn(inputVariantStyles.standard(error), "py-3", icon ? "pl-12 pr-4" : "px-4", showPasswordToggle && "pr-12")}
                    {...props}
                />

                {/* Password Toggle Button */}
                {showPasswordToggle && (
                    <button type="button" onClick={() => setShowPassword((prev) => !prev)} className="absolute inset-y-0 right-0 flex items-center pr-4 text-gray-400 transition-colors hover:text-gray-600" tabIndex={-1} aria-label={showPassword ? "Hide password" : "Show password"}>
                        {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                    </button>
                )}
            </div>

            {/* Helper text or Error message */}
            {(helperText || error) && (
                <p className={cn("text-sm", error ? "text-red-500" : "text-gray-500")} role={error ? "alert" : undefined}>
                    {error || helperText}
                </p>
            )}
        </div>
    )
})

Input.displayName = "Input"
